import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { withStyles } from '@material-ui/styles';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Icon from '@material-ui/core/Icon';
import MenuItem from '@material-ui/core/MenuItem';
import Done from '@material-ui/icons/Done';
import Close from '@material-ui/icons/Close';
import Fab from '@material-ui/core/Fab';
import { reduxForm, Field, SubmissionError } from 'redux-form';
import { required, maxLength, email, isUserUniqueValidation } from 'services/validationMsgs';
import WrappedTextField from 'components/WrappedTextField/WrappedTextField';
import MorePropTypes from 'airbnb-prop-types';
import { RolesForInvites } from '../../../../constants/roles';
import BusyButton from '../../../BusyButton/BusyButton';
import ORTypography from '../../../ORTypography/ORTypography';

const maxLength128 = maxLength(128);

const propTypes = {
  classes: PropTypes.shape({}).isRequired,
  className: PropTypes.string,
  createUserInvite: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  pristine: PropTypes.bool.isRequired,
  reset: PropTypes.func.isRequired,
  valid: PropTypes.bool.isRequired,
  error: MorePropTypes.or([MorePropTypes.explicitNull, PropTypes.string, PropTypes.shape({})]),
};

const defaultProps = {
  error: null,
  className: null,
};

export function userUniqueValidation(value, formValues, props) {
  return isUserUniqueValidation(value, formValues, props.users, props.invites);
}

export function UnstyledInviteUserCardForm({
  classes,
  className,
  createUserInvite,
  handleSubmit,
  pristine,
  reset,
  valid,
  error,
}) {
  function handleCancel() {
    reset();
  }

  /**
   * Appropriate throw an error with the relevant validation errors if necessary
   *
   * @param {result} value - Result of the form submission
   * @throws {error} - Throws SubmissionError if any errors were returned
   */

  const handleFormSubmissionReturn = (result) => {
    if (result.formErrors) {
      throw new SubmissionError(result.formErrors);
    }
    reset();
  };

  /**
   * Submission handler called when the form is executed. Checks
   * for valid api call.
   *
   * @param {object} data - data posted from the user invite form
   * @throws {error} - Throws SubmissionError if data is invalid.
   */

  const submitHandler = (data) => createUserInvite(data).then(handleFormSubmissionReturn);

  const errorElm = error ? (
    <ORTypography color="error" className={classes.formStatus}>
      {error}
    </ORTypography>
  ) : null;

  return (
    <form
      name="myForm"
      onSubmit={handleSubmit(submitHandler)}
      className={classnames(className, classes.inputForm)}
    >
      <Card>
        <CardHeader
          avatar={<Icon className={classes.icon}>add_circle</Icon>}
          title="Invite A User"
          classes={{ action: classes.action, title: classes.title }}
        />
        <CardContent>
          <div>
            <Field
              variant="outlined"
              name="email2invite"
              label="Email"
              placeholder="Email"
              component={WrappedTextField}
              fullWidth
              validate={[maxLength128, required, email, userUniqueValidation]}
              margin="normal"
              InputLabelProps={{
                shrink: true,
              }}
            />
            <Field
              variant="outlined"
              select
              fullWidth
              name="role"
              label="Role"
              component={WrappedTextField}
              placeholder="Select a Role"
              defaultValue="readonly user"
              inputProps={{
                InputLabelProps: {
                  shrink: true,
                  className: classes.inputLabel,
                  defaultValue: 'readonly user',
                },
              }}
              margin="normal"
            >
              {RolesForInvites.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Field>
          </div>
          {errorElm}
        </CardContent>
        <div className={classes.addButtonContainer}>
          <Fab size="small" classes={{ root: classes.cancelButton }} onClick={handleCancel}>
            <Close fontSize="large" />
          </Fab>
          <BusyButton
            disabled={!valid || pristine}
            name="submit"
            type="submit"
            variant="fab"
            color="primary"
            size="small"
            className={classes.addButton}
          >
            <Done fontSize="large" />
          </BusyButton>
        </div>
      </Card>
    </form>
  );
}

UnstyledInviteUserCardForm.propTypes = propTypes;
UnstyledInviteUserCardForm.defaultProps = defaultProps;

const styles = (theme) => ({
  inputForm: {
    marginTop: theme.spacing(1),
  },
  addButtonContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
  },
  addButton: {
    cursor: 'pointer',
    margin: theme.spacing(2),
  },
  action: {
    alignSelf: 'center',
    [theme.breakpoints.down('sm')]: {
      fontSize: '14px',
    },
  },
  expand: {
    transform: 'rotate(0deg)',
    marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  expandOpen: {
    transform: 'rotate(180deg)',
  },
  cancelButton: {
    cursor: 'pointer',
    margin: theme.spacing(2),
    color: theme.customColors.white,
    backgroundColor: theme.customColors.teal,
    '&:hover': {
      background: theme.customColors.teal,
    },
  },
  icon: {
    fontSize: 50,
    color: '#7E3527',
    [theme.breakpoints.down('sm')]: {
      fontSize: 35,
    },
  },
  title: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    [theme.breakpoints.down('sm')]: {
      fontSize: '14px',
    },
  },
});

const formConfig = {
  form: 'userinvite',
  initialValues: {
    email2invite: '',
    role: 'readonly user',
  },
};

export default withStyles(styles)(reduxForm(formConfig)(UnstyledInviteUserCardForm));
